<script setup lang="ts">
const { t: $t } = useI18n()

import type { ApiMachineryDriveGetAll, UpdateOrCreateMachineryType } from '~/types'
import { machineryRubrics } from '~/server/schemas'

const props = defineProps<{
  payload: UpdateOrCreateMachineryType
  machineryDrives: ApiMachineryDriveGetAll[]
  trackerSensorConfigurationOptions: { label: string, value: string }[]
  onSave: (payload: UpdateOrCreateMachineryType) => Promise<unknown>
}>()
const { machineryFieldsByMachineType: showMachineryFieldsByMachineType } = useFormOptionalConditions()
const machineryDrivesOptions = computed(() => props.machineryDrives.map(drive => ({
  label: $t(`machinery.drive.${drive.name}`),
  value: drive.id,
})))
const rubricOptions = useTranslatedSelectOptions('machinery.rubric', machineryRubrics.options)
</script>

<template>
  <FormKit
    v-slot="{ value }"
    :value="payload.data"
    type="form"
    submit-label="Gerätetyp speichern"
    @submit="(data: UpdateOrCreateMachineryType['data']) => onSave({ mode: payload.mode, data } as UpdateOrCreateMachineryType)"
  >
    <TheDevOnlyNiceJson :form-value="value" />
    <FormKit
      name="name"
      type="text"
      validation="required"
      label="Typ"
      placeholder="52-8FDF25 Tonero"
    />

    <FormKitFileDropzone
      name="documentDataSheetFiles"
      label="Datenblatt (deutsch/englisch)"
    />

    <FormKitFileDropzone
      name="documentManualFiles"
      label="Bedienungsanleitung"
    />

    <FormKitFileDropzone
      name="documentVideoFiles"
      label="Videos"
    />

    <FormKitFileDropzone
      name="homepageImages"
      label="Homepage und Shop Bilder"
    />

    <ExternalVideoURLDynamicList
      name="documentExternalVideoFiles"
      label="Externe Videos"
      class="pb-4 !pr-0"
    />

    <n-divider class="my-4" />
    <h3 class="text-base font-semibold mb-2">
      {{ $t('machineryType.creation.title.priceModifierInformation') }}
    </h3>

    <FormKitNumberNaiveUi
      v-if="showMachineryFieldsByMachineType.liftingWeight.includes(payload.data.category)"
      name="liftingWeight"
      step="0.01"
      min="0"
      :label="$t('machinery.field.liftingWeight.name')"
      :placeholder="$t('common.field.dimension.placeholder')"
    />
    <FormKitNumberNaiveUi
      v-if="showMachineryFieldsByMachineType.liftingHeightInMillimeters.includes(payload.data.category)"
      name="liftingHeightInMillimeters"
      step="0.01"
      min="0"
      :label="$t('machinery.field.liftingHeight.name')"
      :placeholder="$t('common.field.dimension.placeholder')"
    />
    <FormKitNumberNaiveUi
      v-if="showMachineryFieldsByMachineType.workHeightInMillimeters.includes(payload.data.category)"
      name="workHeightInMillimeters"
      step="0.01"
      min="0"
      :label="$t('machinery.field.workHeight.name')"
      :placeholder="$t('common.field.dimension.placeholder')"
    />
    <FormKit
      name="machineryRubric"
      type="select"
      :options="rubricOptions"
      :label="$t('machinery.field.rubric.name')"
      :placeholder="$t('machinery.field.rubric.placeholder')"
      class="WidthHalfMinusGap"
    />
    <FormKit
      name="driveId"
      type="select"
      :options="machineryDrivesOptions"
      :label="$t('machinery.field.drive.name')"
      :placeholder="$t('machinery.field.drive.placeholder')"
      class="WidthHalfMinusGap"
    />
    <FormKit
      name="trackerSensorConfigurationId"
      type="select"
      :options="trackerSensorConfigurationOptions"
      :label="$t('machinery.field.trackerSensorConfiguration.name')"
      :placeholder="$t('machinery.field.trackerSensorConfiguration.placeholder')"
      class="WidthHalfMinusGap"
    />
  </FormKit>
</template>
